import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import noop from 'lodash/noop';
import styles from './overlay-wrapper.module.scss';
import type { ComponentName, OverlayManagerContextValue } from '../../types';
import { OverlayManagerContext } from '../../overlay-manager';
import type { IndexName } from '../../overlay-manager.constants';
import {
  VisibilityStates,
  VisibilityState,
} from '../../../../content/pop-out-media-manager/types';
import { getTransformForSide } from '../../overlay-manager.helpers';
import classNames from 'classnames';

export const OverlayAnimationTypes = Object.freeze({
  TranslateFromBottom: 'translate-from-bottom',
  FadeIn: 'fade-in',
});

type AnimationType =
  (typeof OverlayAnimationTypes)[keyof typeof OverlayAnimationTypes];

type OverlayWrapperProps = {
  children: ReactNode;
  onTransitionEnd?: (arg1: VisibilityState) => void;
  componentName: ComponentName;
  indexName: IndexName;
  visible?: boolean;
  animationType?: AnimationType;
};

export const OverlayWrapper = ({
  children,
  onTransitionEnd = noop,
  componentName,
  indexName,
  visible = false,
  animationType = OverlayAnimationTypes.TranslateFromBottom,
}: OverlayWrapperProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [showVisually, setShowVisually] = useState(false);
  const {
    add,
    remove,
    getInsetFor,
    getOverlay,
    updateDimensions,
    updateVisibility,
  } = useContext<OverlayManagerContextValue>(OverlayManagerContext);

  const overlayState = getOverlay(componentName);
  const isVisible = overlayState?.visibility === VisibilityStates.Visible;

  useEffect(() => {
    add({
      componentName,
      indexName,
      ref,
    });

    if (visible) {
      updateVisibility({
        componentName,
        visibility: VisibilityStates.Visible,
      });
    }

    return () => {
      remove({
        componentName,
      });
    };
  }, []);

  useEffect(() => {
    updateVisibility({
      componentName,
      visibility: visible ? VisibilityStates.Visible : VisibilityStates.Hidden,
    });
  }, [visible]);

  useEffect(() => {
    if (isVisible) {
      setShowVisually(true);
    }
  }, [isVisible]);

  const styleObj = isVisible
    ? {
        transform: getTransformForSide(getInsetFor(componentName) || 0),
      }
    : {};

  const handleTransitionend = (evt: React.SyntheticEvent<HTMLDivElement>) => {
    if (!overlayState || evt.target !== ref.current) {
      return;
    }
    if (!isVisible) {
      setShowVisually(false);
    }
    onTransitionEnd(overlayState.visibility);

    // calculate overlay height after it is visible
    updateDimensions({ componentName });
  };

  return (
    <div
      aria-hidden={!isVisible}
      style={styleObj}
      ref={ref}
      onTransitionEnd={handleTransitionend}
      className={classNames(
        styles['overlay-wrapper'],
        styles[`overlay-wrapper--animation-${animationType}`],
        showVisually && styles['overlay-wrapper--visible'],
        styles[`overlay-wrapper--${indexName}`],
      )}
    >
      {children}
    </div>
  );
};
